import { createContext, useContext, useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';

// Single-open accordion: wrap a group of SettingCards so opening one closes the
// others. Provides no DOM — just shares which card id is open.
const AccordionContext = createContext<{
  openId: string | null;
  setOpenId: (id: string | null) => void;
} | null>(null);

export function SettingsAccordion({
  children,
  onOpenChange,
}: {
  children: ReactNode;
  /** Notified with the open card's id (null when all are closed) — lets a card
   *  defer expensive loading until it's actually opened. */
  onOpenChange?: (id: string | null) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const set = (id: string | null) => {
    setOpenId(id);
    onOpenChange?.(id);
  };
  return (
    <AccordionContext.Provider value={{ openId, setOpenId: set }}>
      {children}
    </AccordionContext.Provider>
  );
}

// A collapsible settings card (Arbox pattern): a clickable header (title + chevron),
// an always-visible grey summary line directly under the title, and the controls
// that expand below it. Inside a SettingsAccordion only one card is open at a time.
export function SettingCard({
  id,
  title,
  info,
  summary,
  children,
}: {
  /** Unique within the accordion — identifies which card is open. */
  id: string;
  title: ReactNode;
  /** Optional node next to the title (e.g. an info tooltip). */
  info?: ReactNode;
  /** Grey one-liner describing the current value — always shown, under the title. */
  summary?: ReactNode;
  children: ReactNode;
}) {
  const ctx = useContext(AccordionContext);
  const [localOpen, setLocalOpen] = useState(false);
  const open = ctx ? ctx.openId === id : localOpen;
  const toggle = () => {
    if (ctx) ctx.setOpenId(open ? null : id);
    else setLocalOpen((o) => !o);
  };
  return (
    <div className="border border-border rounded-lg bg-white px-4 py-3 mb-3">
      <button type="button" onClick={toggle} className="w-full flex items-center gap-2 text-start">
        <span
          className="font-medium text-main-text flex items-center gap-1.5"
          style={{ fontSize: 16 }}
        >
          {title}
          {info}
        </span>
        <span className="flex-1" />
        <FontAwesomeIcon
          icon={faChevronDown}
          className="text-secondary-text"
          style={{
            transition: 'transform 0.3s ease-out',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>
      {summary != null && summary !== '' && (
        <div className="text-small text-secondary-text mt-2">{summary}</div>
      )}
      {/* Keep children MOUNTED when collapsed (just hidden) so their form fields
          stay registered — otherwise collapsed cards drop their values on save. */}
      <div className="mt-3" style={{ display: open ? undefined : 'none' }}>
        {children}
      </div>
    </div>
  );
}
